import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { DashList, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `learn.articles.disenar-carta-rentable-restaurante.${f}`

const SOURCES: { labelKey: string; fallback: string }[] = [
    {
        labelKey: 'sources.1',
        fallback:
            'Ejemplo de subida de 0,18 € × 500 platos × 12 meses × 6 referencias (6.480 €/año): elaboración propia de Tipi para ilustrar el efecto acumulado. No es el dato de un local concreto.',
    },
    {
        labelKey: 'sources.2',
        fallback:
            'Matriz de cuatro cuadrantes (mucho/poco volumen × buen/mal margen): criterio operativo habitual de ingeniería de menú adaptado a hostelería independiente. No es un estándar normativo.',
    },
]

export function CartaRentableArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'error', label: t(k('toc.1'), '1. El error de empujar lo que más sale') },
        { id: 'para-que', label: t(k('toc.2'), '2. Para qué sirve saber el coste de cada plato') },
        { id: 'matriz', label: t(k('toc.3'), '3. Los cuatro tipos de plato') },
        { id: 'pasos', label: t(k('toc.4'), '4. Por dónde empezar (y las trampas)') },
        { id: 'rompe', label: t(k('toc.5'), '5. Dónde se rompe el método') },
        { id: 'matices', label: t(k('toc.6'), '6. Lo que la matriz no decide por ti') },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Con costes y márgenes claros puedes destacar lo que deja dinero, modificar lo que vende mal y retirar lo que solo ocupa espacio en carta.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(k('heroAlt'), 'Carta de restaurante sobre una mesa de madera, con un lápiz encima')}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd, 'text-article-muted-soft')}>
                                {t(k('updated'), 'Actualizado el 13 de septiembre de 2026.')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.error'),
                                    'Te llamó el comercial de la app de delivery: «Ponemos en portada tu plato más pedido y te suben las ventas». Lo hiciste. Las ventas subieron. El margen, no. Habías empujado el plato que más salía… y el que menos euros dejaba.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(k('introBefore'), 'Esta pieza cierra la serie: ya sabes ')}
                                <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                    {t(k('introLinkEscandallo'), 'hacer escandallos')}
                                </LearnArticleLink>
                                {t(k('introMid1'), ', calcular ')}
                                <LearnArticleLink slug='food-cost-y-margen-bruto-restaurante'>
                                    {t(k('introLinkFoodCost'), 'food cost y margen')}
                                </LearnArticleLink>
                                {t(k('introMid2'), ' y vigilar el ')}
                                <LearnArticleLink slug='coste-teorico-vs-coste-real-cocina'>
                                    {t(k('introLinkCosteReal'), 'coste real')}
                                </LearnArticleLink>
                                {t(
                                    k('introAfter'),
                                    '. Ahora toca decidir qué platos merecen quedarse, destacarse o salir.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='error' className={articleUi.h2}>
                        {t(k('h2.error'), 'El error de empujar lo que más sale')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.error.1'),
                                'El procedimiento de carta rentable no empieza por rediseñar el PDF. Empieza por dejar de confundir popularidad con rentabilidad. Un plato puede llenar el pase y vaciar el margen.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.error.2'),
                                'La pega va primero: sin coste y margen por plato, cualquier «ingeniería de menú» es decoración. Con esos datos, la matriz de cuatro cuadrantes deja de ser teoría y pasa a ser lista de decisiones.',
                            )}
                        </p>
                    </div>

                    <h2 id='para-que' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.por-que-importante'), 'Para qué sirve saber cuánto cuesta cada plato')}
                    </h2>
                    <div className={articleUi.prose}>
                        <h3 className={articleUi.h3} style={{ marginLeft: 0, marginRight: 0 }}>
                            {t(k('h3.platos-dinero'), 'Saber qué platos te dejan dinero')}
                        </h3>
                        <p className={cn(articleUi.p, articleUi.pGapXl)}>
                            {t(
                                k('p.platos-dinero'),
                                'No los que parecen rentables. No los que más gustan en Instagram. No necesariamente los que más se venden. Sino los que aportan margen de verdad.',
                            )}
                        </p>

                        <h3 className={articleUi.h3} style={{ marginLeft: 0, marginRight: 0 }}>
                            {t(k('h3.subidas'), 'Detectar las subidas antes de que se coman tu beneficio')}
                        </h3>
                        <p className={articleUi.p} style={{ marginBottom: 20 }}>
                            {t(
                                k('p.subidas'),
                                'Una subida de 0,18 € en el coste de un ingrediente puede parecer una tontería. Pero si vendes 500 platos al mes:',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.impactChain}>
                        <span>{t(k('impact.chain.1'), '0,18 € × 500 platos = 90 €/mes')}</span>
                        <span className={articleUi.impactArrow}>→</span>
                        <span>{t(k('impact.chain.2'), '90 € × 12 = 1.080 €/año')}</span>
                        <span className={articleUi.impactArrow}>→</span>
                        <span>{t(k('impact.chain.3'), '1.080 € × 6 platos =')}</span>
                    </div>
                    <p className={articleUi.impactNumber}>6.480 €</p>
                    <p className={articleUi.impactCaption}>{t(k('impact.caption'), 'al año, sin que nadie lo note')}</p>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapSm)}>
                            {t(k('p.impact.before'), 'Ese dinerito quizá sea justo lo que necesitas para:')}
                        </p>
                        <DashList
                            variant='grid-sm'
                            items={[
                                t(k('list.impact.1'), 'Contratar un refuerzo'),
                                t(k('list.impact.2'), 'Comprar una máquina'),
                                t(k('list.impact.3'), 'Mejorar la cocina'),
                                t(k('list.impact.4'), 'Renovar el local'),
                                t(k('list.impact.5'), 'Aumentar tu propio sueldo'),
                                t(k('list.impact.6'), 'Disfrutar de un viaje en familia'),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGapXl)}>
                            {t(
                                k('p.impact.after'),
                                'El problema nunca son solo los 18 céntimos. El problema es no detectarlos y dejar que, plato a plato, se vayan comiendo tu beneficio.',
                            )}
                        </p>

                        <h3 className={articleUi.h3} style={{ marginLeft: 0, marginRight: 0 }}>
                            {t(k('h3.precios'), 'Fijar precios con criterio')}
                        </h3>
                        <p className={cn(articleUi.p, articleUi.pGapXl)}>
                            {t(
                                k('p.precios'),
                                'Ya no subes o mantienes porque «creo que todavía podemos aguantar». Sabes cuánto cuesta el plato y qué efecto tendrá cada decisión sobre el margen.',
                            )}
                        </p>

                        <h3 className={articleUi.h3} style={{ marginLeft: 0, marginRight: 0 }}>
                            {t(k('h3.proveedores'), 'Negociar mejor con los proveedores')}
                        </h3>
                        <p className={cn(articleUi.p, articleUi.pGapXl)}>
                            {t(
                                k('p.proveedores'),
                                'Cuando sabes qué ingredientes pesan en tus costes, sabes dónde merece la pena negociar. Ahorrar 0,20 € en un producto puntual apenas cambia nada. En un ingrediente del que compras cientos de kilos, sí.',
                            )}
                        </p>

                        <h3 className={articleUi.h3} style={{ marginLeft: 0, marginRight: 0 }}>
                            {t(k('h3.desperdicio'), 'Reducir desperdicio y sobreporción')}
                        </h3>
                        <p className={cn(articleUi.p, articleUi.pGapXl)}>
                            {t(
                                k('p.desperdicio'),
                                'Las fichas indican cuánto debe utilizarse. Eso permite detectar si se sirven cantidades de más o si una elaboración no rinde lo previsto.',
                            )}
                        </p>

                        <h3 className={articleUi.h3} style={{ marginLeft: 0, marginRight: 0 }}>
                            {t(k('h3.estabilidad'), 'Dar estabilidad al servicio')}
                        </h3>
                        <p className={articleUi.p} style={{ marginBottom: 0 }}>
                            {t(
                                k('p.estabilidad'),
                                'El cliente recibe el mismo plato, con la misma cantidad y calidad, da igual el turno. Eso mejora el control de costes y la confianza.',
                            )}
                        </p>
                    </div>

                    <h2 id='matriz' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.carta-rentable'), 'Los cuatro tipos de plato (y qué hacer con cada uno)')}
                    </h2>
                    <div className={articleUi.prose} style={{ marginBottom: 24 }}>
                        <p className={articleUi.p} style={{ marginBottom: 0 }}>
                            {t(
                                k('p.carta-rentable.lead'),
                                'Con coste, margen y ventas puedes clasificar. Destaca unos, modifica otros y retira los que solo ocupan espacio y complican la cocina:',
                            )}
                        </p>
                    </div>
                    <div className={articleUi.matrix}>
                        <div className={articleUi.matrixCardGood}>
                            <p>{t(k('matrix.good'), 'Venden mucho y dejan buen margen. Destácalos y protégelos.')}</p>
                        </div>
                        <div className={articleUi.matrixCard}>
                            <p>
                                {t(
                                    k('matrix.highVolLowMargin'),
                                    'Venden mucho, pero dejan poco. Sube precio, recorta coste o cambia receta.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.matrixCard}>
                            <p>
                                {t(
                                    k('matrix.lowVolGoodMargin'),
                                    'Dejan margen, pero apenas se venden. Entrénanos o reubícalos en carta.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.matrixCardBad}>
                            <p>{t(k('matrix.bad'), 'Ni se venden ni dejan dinero. Candidatos a salir.')}</p>
                        </div>
                    </div>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.matriz.caso'),
                                'Caso ilustrativo: un plato a 9,23 € de margen que vende 500 al mes aporta unos 4.615 € de margen de producto. Otro a 11 € de margen que vende 40 aporta 440 €. Si solo miras el margen unitario, empujas el segundo. Si miras euros al mes, el primero paga las nóminas.',
                            )}
                        </p>
                    </div>

                    <h2 id='pasos' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.por-donde-empezar'), 'No escandalles toda la carta en un día')}
                    </h2>
                    <div className={articleUi.prose} style={{ marginBottom: 24 }}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.empezar.1'),
                                'Pensar en 60 o 100 referencias abruma. Y lo abrumador se aplaza. Empieza por donde más dinero se mueve:',
                            )}
                        </p>
                        <DashList
                            variant='tight'
                            items={[
                                t(k('list.empezar.1'), 'Los diez platos más vendidos.'),
                                t(k('list.empezar.2'), 'Los platos con ingredientes más caros.'),
                                t(k('list.empezar.3'), 'Las recetas que llevan más tiempo sin revisarse.'),
                                t(k('list.empezar.4'), 'Los ingredientes que han sufrido subidas recientes.'),
                                t(k('list.empezar.5'), 'Los platos que generan dudas sobre su margen.'),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.empezar.trap'),
                                'Trampa: clasificar con precios de hace un año o sin quitar el IVA. La matriz hereda el error del escandallo. Acción al cerrar cada plato: coste actualizado, margen en euros y unidades del mes.',
                            )}
                        </p>
                        <p className={articleUi.p} style={{ marginBottom: 24 }}>
                            {t(
                                k('p.empezar.2'),
                                'Probablemente encuentres más oportunidades revisando diez platos importantes que escandallando semanas lo que apenas se vende. Después crea una rutina:',
                            )}
                        </p>
                    </div>

                    <div className={cn(articleUi.pills, 'flex-col items-stretch sm:flex-row sm:items-center')}>
                        <span className={cn(articleUi.pill, 'text-center')}>{t(k('pill.1'), 'Revisar precios')}</span>
                        <span className={cn(articleUi.pillArrow, 'hidden text-center sm:inline')}>→</span>
                        <span className={cn(articleUi.pillArrow, 'text-center sm:hidden')}>↓</span>
                        <span className={cn(articleUi.pill, 'text-center')}>
                            {t(k('pill.2'), 'Actualizar escandallos')}
                        </span>
                        <span className={cn(articleUi.pillArrow, 'hidden text-center sm:inline')}>→</span>
                        <span className={cn(articleUi.pillArrow, 'text-center sm:hidden')}>↓</span>
                        <span className={cn(articleUi.pill, 'text-center')}>
                            {t(k('pill.3'), 'Comparar teórico y real')}
                        </span>
                        <span className={cn(articleUi.pillArrow, 'hidden text-center sm:inline')}>→</span>
                        <span className={cn(articleUi.pillArrow, 'text-center sm:hidden')}>↓</span>
                        <span className={cn(articleUi.pill, 'text-center')}>
                            {t(k('pill.4'), 'Analizar ventas y margen')}
                        </span>
                        <span className={cn(articleUi.pillArrow, 'hidden text-center sm:inline')}>→</span>
                        <span className={cn(articleUi.pillArrow, 'text-center sm:hidden')}>↓</span>
                        <span className={cn(articleUi.pill, 'text-center')}>
                            {t(k('pill.5'), 'Aplicar correcciones')}
                        </span>
                    </div>

                    <h2 id='rompe' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.rompe'), 'Dónde se rompe el método')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.rompe.1'),
                                'Se rompe si clasificas con ventas de temporada alta como si fueran el año entero. Se rompe si un plato «malo» es el que trae al cliente que luego pide bebida y postre. Se rompe si tocas precio sin mirar el ticket medio del barrio.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.rompe.2'),
                                'También se rompe cuando la decisión es solo de dueño y cocina sigue porcionando a ojo. La carta rentable sin ficha respetada es un PDF bonito.',
                            )}
                        </p>
                    </div>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'Lo que la matriz no decide por ti')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.1'),
                                'La matriz no te dice si un plato de identidad, aunque deje poco, debe quedarse. Eso es criterio de concepto. Sí te dice cuánto te cuesta mantenerlo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.2'),
                                'Puedes empezar con una hoja de cálculo. Cuando crecen recetas, proveedores y cambios de precio, mantenerlo al día se convierte en otro trabajo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.matices.3Before'), 'Herramientas como ')}
                            <TipiLink />
                            {t(
                                k('p.matices.3After'),
                                ' centralizan escandallos, compras y precios para ver cómo una subida mueve el margen. La herramienta facilita el control. La decisión de qué queda en carta sigue siendo tuya.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.4'),
                                'Si sabes qué platos venden pero no cuáles pagan las nóminas, escríbenos y lo miramos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('closing.signoff'),
                                'Clasifica diez platos esta semana. El resto de la carta puede esperar. El margen, no.',
                            )}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Tu carta vende mucho y deja poco?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi ves qué platos aportan margen de verdad y cuáles solo llenan el pase. Escríbenos y lo miramos.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>{t(k(source.labelKey), source.fallback)}</li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
